import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Validate token and get user info
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.data);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      
      // Vérifier si la réponse contient un token (succès)
      if (response.data && response.data.token) {
        const { token } = response.data;
        localStorage.setItem('token', token);
        
        // Get full user data with permissions
        await loadUser();
        
        // Trigger event for other contexts
        window.dispatchEvent(new Event('userLoggedIn'));
        
        return { success: true };
      } else {
        // La réponse ne contient pas de token, c'est une erreur
        return { 
          success: false, 
          error: response.data?.message || 'Nom d\'utilisateur ou mot de passe incorrect' 
        };
      }
    } catch (error) {
      // Message générique pour des raisons de sécurité
      // Ne pas révéler si c'est le nom d'utilisateur ou le mot de passe qui est incorrect
      let errorMessage = 'Nom d\'utilisateur ou mot de passe incorrect';
      
      // Seulement pour les erreurs autres que 401, utiliser le message d'erreur spécifique
      if (error.response?.status && error.response.status !== 401) {
        errorMessage = error.response?.data?.message 
          || error.response?.data?.error 
          || 'Erreur de connexion';
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  const hasAnyPermission = (permissions) => {
    if (!user || !user.permissions) return false;
    return permissions.some(permission => user.permissions.includes(permission));
  };

  const hasAllPermissions = (permissions) => {
    if (!user || !user.permissions) return false;
    return permissions.every(permission => user.permissions.includes(permission));
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshUser: loadUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};